import { isEqual } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { InvoiceData, LineItem } from "@/apis/gql/types";
import { ValidationRoot } from "@/components/shared/form/shared/form-validation";
import { calculateCheckSums } from "@/lib/utils";
import { invoiceValidationSchema } from "../validations/invoice";

type InvoiceValidationValues = {
  invoiceId: InvoiceData["invoiceId"] | null;
  invoiceNo: InvoiceData["invoiceNo"] | null;
  totalAmount: InvoiceData["totalAmount"] | null;
  currency: InvoiceData["currency"] | null;
  invoiceDate: InvoiceData["invoiceDate"] | null;
  split: InvoiceData["split"] | null;
  isNettoInvoice: InvoiceData["isNettoInvoice"] | null;
  isCorrectAmount: boolean | null;
  serviceDate: InvoiceData["serviceDate"] | null;
  supplierId: InvoiceData["supplierId"] | null;
  lineItems: LineItem[];
};

export const INITIAL_INVOICE_VALIDATION_VALUES: InvoiceValidationValues = {
  invoiceId: null,
  invoiceNo: null,
  totalAmount: null,
  currency: null,
  invoiceDate: null,
  split: null,
  isNettoInvoice: null,
  isCorrectAmount: null,
  serviceDate: null,
  supplierId: null,
  lineItems: [],
};

interface InvoiceValidationContextType {
  setInvoiceValidationData: (invoice: Partial<InvoiceValidationValues>) => void;
}

const InvoiceValidationContext = createContext<
  InvoiceValidationContextType | undefined
>(undefined);

export function InvoiceValidationProvider({
  children,
  invoice,
}: {
  children: React.ReactNode;
  invoice?: Omit<InvoiceValidationValues, "lineItems" | "isCorrectAmount">;
}) {
  const [values, setValues] = useState<InvoiceValidationValues>(
    INITIAL_INVOICE_VALIDATION_VALUES,
  );

  const handleSetInvoiceValidation = useCallback(
    (values: Partial<InvoiceValidationValues>) => {
      setValues((prev: InvoiceValidationValues) => {
        const _values = {
          ...prev,
          ...values,
        };

        const { isNettoInvoice, lineItems, totalAmount } = _values;
        const { isCorrectAmount } = calculateCheckSums(
          totalAmount ?? 0,
          lineItems,
          isNettoInvoice ?? false,
        );

        return {
          ..._values,
          isCorrectAmount,
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (!invoice) return;

    handleSetInvoiceValidation({
      invoiceId: invoice.invoiceId,
      invoiceNo: invoice.invoiceNo,
      totalAmount: invoice.totalAmount,
      currency: invoice.currency,
      invoiceDate: invoice.invoiceDate,
      split: invoice.split,
      serviceDate: invoice.serviceDate,
      supplierId: invoice.supplierId,
    });
  }, [invoice, handleSetInvoiceValidation]);

  if (!invoice || isEqual(values, INITIAL_INVOICE_VALIDATION_VALUES))
    return null;

  return (
    <InvoiceValidationContext.Provider
      value={{ setInvoiceValidationData: handleSetInvoiceValidation }}
    >
      <ValidationRoot schema={invoiceValidationSchema} values={values}>
        {children}
      </ValidationRoot>
    </InvoiceValidationContext.Provider>
  );
}

export const useInvoiceValidation = () => {
  const context = useContext(InvoiceValidationContext);
  if (!context) {
    throw new Error(
      "useInvoiceValidation must be used within InvoiceValidationProvider",
    );
  }
  return context;
};
